import React, { useEffect, useMemo, useRef, useState } from "react";
import { SbTag, SbSpin, SbInfoTooltip } from "uiCore";
import {
  FilterBarWrap,
  FilterLabel,
  FilterSep,
  ClearLink,
  FilterLoadingIndicator,
  FilterAppliedWrap,
  DimSelect,
  ValueSelect,
} from "../Dashboard.styles";

const TOOLTIP_FILTER_BY =
  "Segment every widget on this dashboard by a single dimension — pick a dimension, then a value, to see potential value, hours, collaboration split, and ROI for just that segment.";

const FilterBar = ({ dimensions, activeDim, activeIndex, onDimChange, onValueChange, onClear, loading, highlighted }) => {
  const dimOptions = useMemo(
    () => Object.entries(dimensions || {}).map(([key, dim]) => ({ value: key, label: dim.label })),
    [dimensions],
  );

  const valueOptions = useMemo(
    () => (activeDim && dimensions?.[activeDim] ? dimensions[activeDim].values.map((v, i) => ({ value: i, label: v })) : []),
    [dimensions, activeDim],
  );

  const isFiltered = Boolean(activeDim) && activeIndex >= 0;
  // Identifies the actual applied filter (dimension + value), not just
  // whether one is applied — so changing the value or dimension while
  // already filtered counts as a new application too, not only the first
  // unfiltered-to-filtered transition.
  const filterKey = isFiltered ? `${activeDim}:${activeIndex}` : null;

  // Pulse the applied-filter tag once each time a (new) filter is applied,
  // so the user gets a clear visual cue that their selection took effect.
  const [justApplied, setJustApplied] = useState(false);
  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    const prevFilterKey = prevFilterKeyRef.current;
    prevFilterKeyRef.current = filterKey;
    if (filterKey && filterKey !== prevFilterKey) {
      setJustApplied(true);
      const timer = setTimeout(() => setJustApplied(false), 900);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [filterKey]);

  return (
    <FilterBarWrap $highlighted={highlighted}>
      <FilterLabel>
        <SbInfoTooltip content={TOOLTIP_FILTER_BY}>Filter by</SbInfoTooltip>
      </FilterLabel>
      <DimSelect
        placeholder="Segment by"
        value={activeDim || undefined}
        options={dimOptions}
        onChange={(value) => onDimChange(value)}
      />
      <ValueSelect
        placeholder="— Select value —"
        value={isFiltered ? activeIndex : undefined}
        options={valueOptions}
        onChange={(value) => onValueChange(value)}
        disabled={!activeDim}
      />
      {loading && (
        <FilterLoadingIndicator>
          <SbSpin size="small" />
          Updating...
        </FilterLoadingIndicator>
      )}
      <FilterSep />
      {isFiltered && (
        <FilterAppliedWrap $pulse={justApplied}>
          <SbTag closable onClose={onClear} color="processing">
            {dimensions[activeDim].label}: {dimensions[activeDim].values[activeIndex]}
          </SbTag>
        </FilterAppliedWrap>
      )}
      {isFiltered && <ClearLink onClick={onClear}>Clear</ClearLink>}
    </FilterBarWrap>
  );
};

export default FilterBar;
